<template>
    <base-custom-settings :form-data="formData" save-message="De e-mail is aangepast">
        <template #description>
            <text-heading
                sub
            >
                E-mail versturen
            </text-heading>
            <text-description
                :color="'white'"
            >
                De e-mail is al volledig opgemaakt in jouw huisstijl en staat klaar voor gebruik. Via dit scherm heb je
                de mogelijkheid om zowel de e-mail als eventuele verwijzingen naar landingspagina’s te bekijken en
                indien nodig aan te passen. De landingspagina is eveneens volledig voorbereid in de huisstijl, compleet
                met alle content. Je hebt de volledige controle om alles naar wens te finetunen. Echter, alles is al
                tot in de puntjes verzorgd, dus je kunt direct van start gaan zonder noodzaak tot aanpassingen.
            </text-description>
        </template>

        <template #form>
            <inline-tabs selected-name="email">
                <template #labels>
                    <inline-tab-label name="email">
                        E-mail
                    </inline-tab-label>
                    <inline-tab-label name="pages">
                        Pagina's
                    </inline-tab-label>
                </template>

                <template #panels>
                    <tab-panel-email
                        v-model:sendTime="formData.data.sendTime"
                        v-model:subject="formData.data.subject"
                        v-model:headerText="formData.data.headerText"
                    />

                    <tab-panel-pages
                        v-model:pages="pages"
                    />
                </template>
            </inline-tabs>
        </template>

        <template #buttons>
            <button-text :disabled="!beefreeToken" @click="openEmailEditor">
                <flex-container :gap="Size.sm" align-items="center">
                    <svg-icon
                        :icon="IconPenToSquare"
                        :size="IconSize.sm"
                        is-custom-icon
                    />
                    <span>E-mail aanpassen</span>
                </flex-container>
            </button-text>
        </template>
    </base-custom-settings>

    <fullscreen-dialog
        v-if="currentCompany && beefreeToken"
        ref="emailEditorDialog"
        label="E-mail bewerken"
        description="E-mail bewerken"
        without-padding
        hide-close
        :size="7"
    >
        <template #default>
            <email-editor
                v-if="emailEditorDialog?.isOpen"
                :token="beefreeToken"
                :company="currentCompany"
                :subject="formData.data.subject"
                :template="formData.data.template"
                :merge-tags="availableMergeTags"
                :merge-content="availableMergeContent"
                :special-links="availableSpecialLinks"
                :display-conditions="availableDisplayConditions"
                @back="closeEmailEditor"
                @save="emailEditorOnSave"
                @update-subject="emailEditorOnSubjectUpdate"
            />
        </template>
    </fullscreen-dialog>
</template>

<script setup lang="ts">
    import {
        Size,
        IconSize,
        FlexContainer,
        ButtonText,
        SvgIcon,
        InlineTabs,
        InlineTabLabel,
        FullscreenDialog,
        type ExposedOverlay,
        TextDescription,
        TextHeading,
    } from '@keyvue/one-ui';

    import {
        getMergeTagsForTrigger,
    } from '@/flow/flow/utils/merge-tags';

    import {
        getMergeContentForTrigger,
    } from '@/flow/flow/utils/merge-content';

    import {
        getUnsubscribeSpecialLink,
        getSpecialLinksForPages,
        getSpecialLinksForTrigger,
        getSpecialLinksForBrand,
    } from '@/flow/flow/utils/special-links';

    import {
        getContactDisplayConditions,
        getContactGenderDisplayConditions,
        getTeamDisplayConditions,
    } from '@/flow/flow/utils/display-conditions';

    import { type Flow, GetBeefreeTokenCommand, type Node, Page, Team } from '@keyvue/one-api-client';
    import { computed, onMounted, reactive, ref, type Ref } from 'vue';
    import { useRouter } from 'vue-router';
    import useClient from '@/services/keyvue-api/composables/useClient';

    import IconPenToSquare from '@keyvue/one-awesome-icons/icons/regular/pen-to-square.svg?component';
    import EmailEditor, { type BeefreeSaveEvent } from '@/flow/flow/components/email-editor/EmailEditor.vue';
    import BaseCustomSettings from '@/flow/flow/components/graph/custom-types/CustomSettings.vue';
    import TabPanelEmail from '@/flow/flow/components/graph/custom-types/NodeActionSendEmail/tabs/TabPanelEmail.vue';
    import TabPanelPages from '@/flow/flow/components/graph/custom-types/NodeActionSendEmail/tabs/TabPanelPages.vue';

    interface NodeOptions {
        action: 'send-email'
        data: {
            sendTime: string
            subject: string
            headerText: string
            template: string
            html: string
        }
    }

    interface Props {
        node: Node
        flow: Flow
    }

    const emit = defineEmits<{
        (e: 'saveNode', options: any, stopEditing?: boolean, message?: string): void;
    }>();

    const emailEditorDialog = ref<ExposedOverlay>();
    const props = defineProps<Props>();
    const router = useRouter();
    const { client, currentCompany } = useClient();

    const beefreeToken = ref<string>();
    const teams = ref<Team[]>() as Ref<Team[]>;

    const formData = reactive({
        action: 'send-email',
        data: {
            sendTime: props.node.options?.data?.sendTime ?? '08:00',
            subject: props.node.options?.data?.subject ?? '',
            headerText: props.node.options?.data?.headerText ?? '',
            template: props.node.options?.data?.template ?? '{}',
            html: props.node.options?.data?.html ?? '',
        },
    } as NodeOptions);

    const pages = ref<Page[]>(props.node.pages) as Ref<Page[]>;

    function openEmailEditor() {
        emailEditorDialog.value?.openOverlay();
    }

    function closeEmailEditor() {
        emailEditorDialog.value?.closeOverlay();
    }

    function emailEditorOnSave(payload: BeefreeSaveEvent) {
        formData.data.template = payload.pageJson;
        formData.data.html = payload.pageHtml;

        emailEditorDialog.value?.closeOverlay();
        emit('saveNode', formData, false, 'De e-mail is aangepast');
    }

    function emailEditorOnSubjectUpdate(changedName: string) {
        formData.data.subject = changedName;

        emit('saveNode', formData, false, 'De e-mail is aangepast');
    }

    async function loadBeefreeToken() {
        beefreeToken.value = await client.dispatch(
            new GetBeefreeTokenCommand(currentCompany.value.id),
        );
    }

    async function loadTeams() {
        const teamsSubResources = await client.getSubResources(currentCompany.value, Team, 'teams');

        teams.value = teamsSubResources.data;
    }

    const availableMergeTags = computed(() => {
        return [
            ...props.flow.triggers.flatMap(getMergeTagsForTrigger),
        ];
    });

    const availableMergeContent = computed(() => {
        return [
            ...props.flow.triggers.flatMap(getMergeContentForTrigger),
        ];
    });

    const availableSpecialLinks = computed(() => {
        return [
            getUnsubscribeSpecialLink(),
            ...getSpecialLinksForBrand(props.flow.brand),
            ...getSpecialLinksForPages(props.node, router),
            ...props.flow.triggers.flatMap(getSpecialLinksForTrigger),
        ];
    });

    const availableDisplayConditions = computed(() => {
        return [
            ...getContactDisplayConditions(),
            ...getContactGenderDisplayConditions(),
            ...getTeamDisplayConditions(teams.value),
        ];
    });

    onMounted(() => {
        Promise.all([
            loadBeefreeToken(),
            loadTeams(),
        ]);
    });
</script>

<style scoped lang="scss">
:deep(.inline-tabs__labels) {
    justify-content: center;
}
</style>
